import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { V3_BASE_URL } from '@appmaker/core/api/core';
import { useMemo, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { getProjectData } from '@appmaker/core/store/project';

const P_ENGAGE_BASE_PATH = '/p-engage';

const DEFAULT_PARAMS = {
    page: '1',
};

// Helper function to get app key from project data
// TODO: Update this to get the actual app_key from your project configuration
const getAppKey = () => {
    const { projectData } = getProjectData();
    // This is a placeholder - replace with actual app_key field from your project data
    return projectData?.app_key || projectData?.meta?.app_key || 'placeholder-app-key';
};

// Helper function to get store URL from project data
// TODO: Update this to get the actual store URL from your project configuration
const getStoreUrl = () => {
    const { projectData } = getProjectData();
    // This is a placeholder - replace with actual store URL field from your project data
    return projectData?.store_url || projectData?.url || projectData?.meta?.store_url || 'placeholder-store.myshopify.com';
};

/**
 * @typedef {Object} NotificationResponse
 * @property {boolean} success - Whether the operation was successful
 * @property {string} message - Response message
 * @property {Object} data - Response data
 */

/**
 * @typedef {Object} ScheduledNotificationResponse
 * @property {boolean} success - Whether the operation was successful
 * @property {string} message - Response message
 * @property {Object} data - Scheduled notification data
 * @property {string} data.id - Notification ID
 * @property {string} data.status - Notification status
 */

/**
 * @typedef {Object} CustomerIdResponse
 * @property {boolean} success - Whether the operation was successful
 * @property {string} customer_id - Customer ID
 */

/**
 * @typedef {Object} TestNotificationResponse
 * @property {boolean} success - Whether the test was successful
 * @property {string} message - Response message
 * @property {number} sent_count - Number of notifications sent
 */

// Optimized fetch function with better error handling and request cancellation support
const fetchEngageData = async (projectId, endpoint, params, signal, method = 'GET', data = null) => {
    if (!projectId) {
        return Promise.reject(new Error("Project ID is required"));
    }
    
    const url = `${V3_BASE_URL}/projects/${projectId}${P_ENGAGE_BASE_PATH}${endpoint}`;
    

    
    try {
        const config = {
            method,
            url,
            withCredentials: true,
            signal,
            timeout: 30000,
        };

        if (method === 'GET') {
            config.params = params;
        } else {
            config.data = data;
            if (params && Object.keys(params).length > 0) {
                config.params = params;
            }
        }

        const response = await axios(config);
        
        return response.data;
    } catch (error) {
        // Don't log if request was cancelled
        if (error.name !== 'CanceledError') {
            console.error(`Error ${method} data from ${url}:`, error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
        }
        throw error;
    }
};

// Memoized query key factory to prevent unnecessary re-renders
const createQueryKey = (endpoint, projectId, params) => {
    return [endpoint, projectId, JSON.stringify(params)];
};

/**
 * Fetches notification history.
 * @param {string} projectId - The ID of the project.
 * @param {object} queryParams - Optional query parameters to override defaults.
 * @param {string} queryParams.page - Page number for pagination
 * @param {string} queryParams.search - Search query for filtering notifications
 * @param {string} queryParams.filter_by - Filter parameter in format "field:value" (e.g., "status:scheduled")
 * @param {object} options - React Query options.
 * @returns {Object} React Query result with notification history data
 */
export const useNotificationHistory = (projectId, queryParams = {}, options = {}) => {
    const params = useMemo(() => {
        const baseParams = { 
            page: queryParams.page || DEFAULT_PARAMS.page,
        };
        
        // Add search parameter if provided
        if (queryParams.search && queryParams.search.trim()) {
            baseParams.search = queryParams.search.trim();
        }
        
        // Add filter_by parameter if provided
        if (queryParams.filter_by && queryParams.filter_by.trim()) {
            baseParams.filter_by = queryParams.filter_by.trim();
        }
        
        return baseParams;
    }, [queryParams]);
    
    const queryKey = useMemo(() => createQueryKey('notificationHistory', projectId, params), [projectId, params]);
    
    return useQuery(
        queryKey,
        ({ signal }) => {
            return fetchEngageData(projectId, '/history', params, signal);
        },
        {
            enabled: !!projectId,
            staleTime: 2 * 60 * 1000, // 2 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false,
            retry: 2,
            ...options
        }
    );
};

/**
 * Fetches scheduled notifications.
 * @param {string} projectId - The ID of the project.
 * @param {object} queryParams - Optional query parameters to override defaults.
 * @param {object} options - React Query options.
 * @returns {Object} React Query result with scheduled notifications data
 */
export const useScheduledNotifications = (projectId, queryParams = {}, options = {}) => {
    const params = useMemo(() => ({}), [queryParams]);
    
    const queryKey = useMemo(() => createQueryKey('scheduledNotifications', projectId, params), [projectId, params]);
    
    return useQuery(
        queryKey,
        ({ signal }) => fetchEngageData(projectId, '/scheduled-notification', params, signal),
        {
            enabled: !!projectId,
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false,
            retry: 2,
            ...options
        }
    );
};

/**
 * Fetches campaign insights and analytics with performance metrics.
 * @param {string} projectId - The ID of the project.
 * @param {object} queryParams - Query parameters with from and to dates.
 * @param {string} queryParams.from - Start date for insights (YYYY-MM-DD format)
 * @param {string} queryParams.to - End date for insights (YYYY-MM-DD format)
 * @param {object} options - React Query options.
 * @returns {Object} React Query result with campaign insights data
 * @example
 * const { data, isLoading, error } = useCampaignInsights('project-123', {
 *   from: '2025-01-01',
 *   to: '2025-01-31'
 * }, {
 *   onSuccess: (data) => console.log('Insights loaded:', data),
 *   onError: (error) => console.error('Failed to load insights:', error)
 * });
 * 
 * @typedef {Object} CampaignInsightSequence
 * @property {number} id - Sequence ID
 * @property {string} title - Sequence title
 * @property {string} message - Sequence message content
 * @property {number} sequence - Sequence order number
 * @property {string} trigger_unit - Time unit for trigger (MINUTES, HOURS, DAYS)
 * @property {number} trigger_value - Trigger value in the specified unit
 * @property {number} trigger_time_minutes - Total trigger time in minutes
 * 
 * @typedef {Object} CampaignInsightData
 * @property {string} slug - Campaign slug (WELCOME_USER, ABANDONED_CART, ABANDONED_CHECKOUT, INACTIVE_USER, REMOVED_FROM_CART)
 * @property {number} delivered - Number of delivered notifications
 * @property {number} clicked - Number of clicked notifications
 * @property {number} opened - Number of opened notifications
 * @property {number} converted - Number of conversions
 * @property {string} status - Campaign status (ACTIVE, PAUSED)
 * @property {CampaignInsightSequence[]} sequences - Array of campaign sequences
 * 
 * @typedef {Object} CampaignInsightsResponse
 * @property {number} error - Error code (0 for success)
 * @property {number} status - HTTP status code
 * @property {string} message - Response message
 * @property {Array} scopes - API scopes
 * @property {CampaignInsightData[]} data - Array of campaign insights
 * @property {number} total_pages - Total pages (usually 0)
 * @property {number} total_elements - Total elements (usually 0)
 * @property {number} number_of_elements - Number of elements (usually 0)
 */
export const useCampaignInsights = (projectId, queryParams = {}, options = {}) => {
    const params = useMemo(() => ({ 
        from: queryParams.from,
        to: queryParams.to,
    }), [queryParams]);
    
    const queryKey = useMemo(() => createQueryKey('campaignInsights', projectId, params), [projectId, params]);
    
    return useQuery(
        queryKey,
        ({ signal }) => fetchEngageData(projectId, '/campaigns/insights', params, signal),
        {
            enabled: !!projectId && !!params.from && !!params.to,
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false,
            retry: 2,
            ...options
        }
    );
};

/**
 * Fetches customer ID by email.
 * @param {string} projectId - The ID of the project.
 * @param {object} queryParams - Query parameters including email.
 * @param {string} queryParams.email - Email to get customer ID for.
 * @param {object} options - React Query options.
 * @returns {Object} React Query result with customer ID data
 */
export const useCustomerId = (projectId, queryParams = {}, options = {}) => {
    const params = useMemo(() => ({ 
        email: queryParams.email,
        app_key: getAppKey(),
    }), [queryParams.email]);
    
    const queryKey = useMemo(() => createQueryKey('customerId', projectId, params), [projectId, params]);
    
    return useQuery(
        queryKey,
        ({ signal }) => fetchEngageData(projectId, '/pnl/customer-id', params, signal),
        {
            enabled: !!projectId && !!params.email,
            staleTime: 10 * 60 * 1000, // 10 minutes - customer IDs don't change often
            cacheTime: 30 * 60 * 1000, // 30 minutes
            refetchOnWindowFocus: false,
            retry: 2,
            ...options
        }
    );
};

/**
 * Fetches app campaigns with their sequences and insights.
 * @param {string} projectId - The ID of the project.
 * @param {object} queryParams - Optional query parameters to override defaults.
 * @param {object} options - React Query options.
 * @returns {Object} React Query result with campaigns data
 * @example
 * const { data, isLoading, error } = useCampaigns('project-123', {}, {
 *   onSuccess: (data) => console.log('Campaigns loaded:', data),
 *   onError: (error) => console.error('Failed to load campaigns:', error)
 * });
 * 
 * @typedef {Object} CampaignInsight
 * @property {number} id - Insight ID
 * @property {number} campaign_id - Campaign ID
 * @property {string} date - Date in YYYY-MM-DD format
 * @property {number} delivered - Number of delivered notifications
 * @property {number} clicked - Number of clicked notifications
 * @property {number} opened - Number of opened notifications
 * @property {number} converted - Number of conversions
 * 
 * @typedef {Object} CampaignSequence
 * @property {number} id - Sequence ID
 * @property {string} title - Sequence title
 * @property {string} message - Sequence message content
 * @property {number} sequence - Sequence order number
 * @property {string} trigger_unit - Time unit for trigger (MINUTES, HOURS, DAYS)
 * @property {number} trigger_value - Trigger value in the specified unit
 * @property {number} trigger_time_minutes - Total trigger time in minutes
 * 
 * @typedef {Object} Campaign
 * @property {number} id - Campaign ID
 * @property {number} app_id - App ID
 * @property {string} slug - Campaign slug (WELCOME_USER, ABANDONED_CART, ABANDONED_CHECKOUT, INACTIVE_USER, REMOVED_FROM_CART)
 * @property {string|null} created_at - Creation timestamp
 * @property {string} status - Campaign status (ACTIVE, PAUSED)
 * @property {string} updated_at - Last update timestamp
 * @property {CampaignInsight[]} campaign_insights - Array of campaign insights
 * @property {CampaignSequence[]} campaign_sequences - Array of campaign sequences
 * 
 * @typedef {Object} CampaignsResponse
 * @property {number} error - Error code (0 for success)
 * @property {number} status - HTTP status code
 * @property {string} message - Response message
 * @property {Array} scopes - API scopes
 * @property {Object} data - Response data
 * @property {Campaign[]} data.campaigns - Array of campaigns
 * @property {number} total_pages - Total pages (usually 0)
 * @property {number} total_elements - Total elements (usually 0)
 * @property {number} number_of_elements - Number of elements (usually 0)
 */
export const useCampaigns = (projectId, queryParams = {}, options = {}) => {
    const params = useMemo(() => ({}), [queryParams]);
    
    const queryKey = useMemo(() => createQueryKey('campaigns', projectId, params), [projectId, params]);
    
    return useQuery(
        queryKey,
        ({ signal }) => fetchEngageData(projectId, '/campaigns/app', params, signal),
        {
            enabled: !!projectId,
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false,
            retry: 2,
            ...options
        }
    );
};

/**
 * Creates an immediate push notification.
 * @param {object} options - React Query mutation options.
 * @returns {Object} React Query mutation for creating notifications
 * @example
 * const mutation = useCreateNotification({
 *   onSuccess: (data) => console.log('Notification sent:', data),
 *   onError: (error) => console.error('Failed:', error)
 * });
 * 
 * mutation.mutate({
 *   projectId: 'project-123',
 *   data: {
 *     feature_id: 26,
 *     app_feature_id: 26,
 *     icon_path: "123",
 *     notification_type: "custom",
 *     post_title: "Hey There",
 *     message: "Hey There",
 *     app_deep_link: {
 *       resource: "products",
 *       id: "8352112476381",
 *       title: "Tshirt"
 *     },
 *     timezone: "America/New_York",
 *     image_url: "https://example.com/image.png",
 *     title: "Tshirt"
 *   }
 * });
 */
export const useCreateNotification = (options = {}) => {
    return useMutation(
        async ({ projectId, data, signal }) => {
            return fetchEngageData(projectId, '/notification', {}, signal, 'POST', data);
        },
        {
            retry: options.retry !== undefined ? options.retry : 0, // Default to no retry, allow override
            ...options
        }
    );
};

/**
 * Creates a scheduled push notification.
 * @param {object} options - React Query mutation options.
 * @returns {Object} React Query mutation for creating scheduled notifications
 * @example
 * const mutation = useCreateScheduledNotification({
 *   onSuccess: (data) => console.log('Scheduled notification created:', data),
 *   onError: (error) => console.error('Failed:', error)
 * });
 * 
 * mutation.mutate({
 *   projectId: 'project-123',
 *   data: {
 *     feature_id: "24",
 *     app_feature_id: "24.1441716117",
 *     icon_path: "https://example.com/icon.png",
 *     notification_type: "custom",
 *     post_title: "Hey There",
 *     message: "Hey There",
 *     app_deep_link: {},
 *     timezone: "America/New_York",
 *     time: "5:15 AM",
 *     start_date: "05/22/2025",
 *     end_date: "05/31/2025",
 *     frequency: "weekly",
 *     image_url: "https://example.com/image.png",
 *     title: "Work Daily"
 *   }
 * });
 */
export const useCreateScheduledNotification = (options = {}) => {
    return useMutation(
        async ({ projectId, data, signal }) => {
            return fetchEngageData(projectId, '/scheduled-notification', {}, signal, 'POST', data);
        },
        {
            retry: options.retry !== undefined ? options.retry : 0, // Default to no retry, allow override
            ...options
        }
    );
};

/**
 * Updates a scheduled notification (pause/resume).
 * @param {object} options - React Query mutation options.
 * @returns {Object} React Query mutation for updating scheduled notifications
 */
export const useUpdateScheduledNotification = (options = {}) => {
    return useMutation(
        async ({ projectId, data, signal }) => {
            return fetchEngageData(projectId, '/scheduled-notification', {}, signal, 'PUT', data);
        },
        {
            retry: options.retry !== undefined ? options.retry : 0, // Default to no retry, allow override
            ...options
        }
    );
};

/**
 * Updates app campaigns with sequences and status.
 * @param {object} options - React Query mutation options.
 * @returns {Object} React Query mutation for updating campaigns
 * @example
 * const mutation = useUpdateCampaigns({
 *   onSuccess: (data) => console.log('Campaign updated:', data),
 *   onError: (error) => console.error('Failed:', error)
 * });
 * 
 * mutation.mutate({
 *   projectId: 'project-123',
 *   data: {
 *     slug: "WELCOME_USER",
 *     sequences: [
 *       {
 *         id: 105,
 *         title: "Welcome Message",
 *         message: "Welcome to our app!",
 *         sequence: 1,
 *         trigger_unit: "HOURS",
 *         trigger_value: 12,
 *         trigger_time_minutes: 720
 *       },
 *       {
 *         id: 1,
 *         title: "Follow Up",
 *         message: "How are you enjoying the app?",
 *         sequence: 2,
 *         trigger_unit: "MINUTES",
 *         trigger_value: 2,
 *         trigger_time_minutes: 2
 *       }
 *     ],
 *     status: "ACTIVE"
 *   }
 * });
 * 
 * @typedef {Object} CampaignSequence
 * @property {number} id - Sequence ID
 * @property {string} title - Sequence title
 * @property {string} message - Sequence message content
 * @property {number} sequence - Sequence order number
 * @property {string} trigger_unit - Time unit for trigger (MINUTES, HOURS, DAYS)
 * @property {number} trigger_value - Trigger value in the specified unit
 * @property {number} trigger_time_minutes - Total trigger time in minutes
 * 
 * @typedef {Object} UpdateCampaignPayload
 * @property {string} slug - Campaign slug (WELCOME_USER, ABANDONED_CART, ABANDONED_CHECKOUT, INACTIVE_USER, REMOVED_FROM_CART)
 * @property {CampaignSequence[]} sequences - Array of campaign sequences
 * @property {string} status - Campaign status (ACTIVE, PAUSED)
 * 
 * @typedef {Object} UpdateCampaignResponse
 * @property {number} error - Error code (0 for success)
 * @property {number} status - HTTP status code
 * @property {string} message - Response message
 * @property {Array} scopes - API scopes
 * @property {Object} data - Updated campaign data
 * @property {number} data.id - Campaign ID
 * @property {number} data.app_id - App ID
 * @property {string} data.slug - Campaign slug
 * @property {string|null} data.created_at - Creation timestamp
 * @property {string} data.status - Campaign status
 * @property {string} data.updated_at - Last update timestamp
 * @property {Array} data.campaign_insights - Campaign insights data
 * @property {CampaignSequence[]} data.campaign_sequences - Updated campaign sequences
 * @property {number} total_pages - Total pages (usually 0 for single item)
 * @property {number} total_elements - Total elements (usually 0 for single item)
 * @property {number} number_of_elements - Number of elements (usually 0 for single item)
 */
export const useUpdateCampaigns = (options = {}) => {
    return useMutation(
        async ({ projectId, data, queryParams = {}, signal }) => {
            const params = {};
            return fetchEngageData(projectId, '/campaigns/app', params, signal, 'POST', data);
        },
        {
            retry: options.retry !== undefined ? options.retry : 0, // Default to no retry, allow override
            ...options
        }
    );
};

/**
 * Sends a test notification to specific customer IDs.
 * @param {object} options - React Query mutation options.
 * @returns {Object} React Query mutation for sending test notifications
 * @example
 * const mutation = useSendTestNotification({
 *   onSuccess: (data) => console.log('Test sent:', data),
 *   onError: (error) => console.error('Failed:', error)
 * });
 * 
 * mutation.mutate({
 *   projectId: 'project-123',
 *   data: {
 *     title: "hey There",
 *     body: "Hey There ",
 *     action: "",
 *     resource: "products",
 *     resource_id: "8352112476381",
 *     resource_title: "Tshirt",
 *     app_feature_id: "",
 *     feature_id: "",
 *     customer_ids: ["7115022074077"]
 *   }
 * });
 */
export const useSendTestNotification = (options = {}) => {
    return useMutation(
        async ({ projectId, data, queryParams = {}, signal }) => {
            const params = {
                app_key: queryParams.app_key || getAppKey(),
            };
            return fetchEngageData(projectId, '/test-notification', params, signal, 'POST', data);
        },
        {
            retry: options.retry !== undefined ? options.retry : 0, // Default to no retry, allow override
            ...options
        }
    );
};

/**
 * Custom hook to fetch customer IDs for multiple emails
 * @param {string} projectId - The ID of the project
 * @param {string[]} emails - Array of email addresses
 * @returns {Object} Object containing customerIds, isLoading, and error
 */
export const useBatchCustomerIds = (projectId, emails) => {
    const [customerIds, setCustomerIds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCustomerIds = async () => {
            if (!projectId || !emails || emails.length === 0) {
                setCustomerIds([]);
                setError(null);
                return;
            }

            setIsLoading(true);
            setError(null);
            const results = [];
            const failedEmails = [];

            for (const email of emails) {
                try {
                    const response = await fetchEngageData(
                        projectId,
                        '/customer-id',
                        { email, app_key: getAppKey() },
                        null,
                        'GET'
                    );
                    console.log('responsexxxxxx', response);
                    
                    // Handle different response structures
                    let customerId = null;
                    if (response?.data?.customer_id) {
                        customerId = response.data.customer_id;
                    } else if (response?.customer_id) {
                        customerId = response.customer_id;
                    }
                    
                    if (customerId) {
                        results.push(customerId);
                    } else {
                        console.warn(`No customer ID found for email: ${email}`, response);
                        failedEmails.push(email);
                    }
                } catch (err) {
                    console.error(`Failed to get customer ID for ${email}:`, err);
                    failedEmails.push(email);
                }
            }

            setCustomerIds(results);
            if (failedEmails.length > 0) {
                setError({
                    message: `Failed to get customer IDs for: ${failedEmails.join(', ')}`,
                    failedEmails
                });
            } else {
                setError(null);
            }
            setIsLoading(false);
        };

        fetchCustomerIds();
    }, [projectId, emails]);

    return { customerIds, isLoading, error };
}; 